import React, { useState, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import Header from "../../main/components/Header";
import MobileBottomBar from "../../main/components/MobileBottomBar";
import Menu from "../components/Menu";
import WalletTopUp from "../components/WalletTopUp";
import BuyPackage from "../components/BuyPackage";
import PaymentHistory from "../components/PaymentHistory";
import Advertisements from "../components/Advertisements";
import Chats from "../components/Chats";
import Settings from "../components/Settings";
import Favorites from "../components/Favorites";
import ContractTemplates from "../components/ContractTemplates";
import TariffsContent from '../../tariffs/components/TariffsContent';
import CounterpartyCheck from "../components/CounterpartyCheck";
import Finance from "../components/Finance";
import MyReviewsContent from "../components/MyReviewsContent.js";
import DealerEmployees from "../components/DealerEmployees";
import Path from "../../path/ui/Path";

import { useTranslation } from "react-i18next";

function Main() {
    const location = useLocation();
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState();
    const [hideBottomBar, setHideBottomBar] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 786);

    const tabComponents = {
        walletTopUp: <WalletTopUp setActiveTab={setActiveTab} />,
        purchasePackage: <BuyPackage setActiveTab={setActiveTab} />,
        paymentHistory: <PaymentHistory setActiveTab={setActiveTab} />,
        ads: <Advertisements setActiveTab={setActiveTab} />,
        messages: <Chats hideBottomBar={(isHidden) => setHideBottomBar(isHidden)} />,
        settings: <Settings setActiveTab={setActiveTab} />,
        favorites: <Favorites />,
        contractTemplates: <ContractTemplates setActiveTab={setActiveTab} />,
        myReviews: <MyReviewsContent setActiveTab={setActiveTab} />,
        tariffs: <TariffsContent setActiveTab={setActiveTab} />,
        counterpartyCheck: <CounterpartyCheck setActiveTab={setActiveTab} />,
        finance: <Finance setActiveTab={setActiveTab} />,
        dealerEmployees: <DealerEmployees setActiveTab={setActiveTab} />
    };

    const tabNames = useMemo(() => ({
        walletTopUp: t('replenishWallet'),
        purchasePackage: t('buyPackage'),
        paymentHistory: t('paymentHistory'),
        ads: t('announcements'),
        messages: t('messages'),
        settings: t('settings'),
        favorites: t('favorites'),
        contractTemplates: t('contractTemplates'),
        myReviews: t('feedbacks'),
        tariffs: t('tariffs'),
        counterpartyCheck: t('counterpartyCheck'),
        finance: t('finance'),
        dealerEmployees: t('employees')
    }), [t]);

    useEffect(() => {
        const updateActiveTab = () => {
            const hash = window.location.hash.replace('#', '');
            if (tabNames[hash]) {
                setActiveTab(hash);
            } else {
                setActiveTab(null);
            }
        };
        
        // Initial load
        updateActiveTab();
        
        // Listen to hash changes
        const handleHashChange = () => {
            updateActiveTab();
        };
        
        window.addEventListener('hashchange', handleHashChange);
        
        // Also listen to location changes from React Router
        const hash = location.hash.replace('#', '');
        if (tabNames[hash]) {
            setActiveTab(hash);
        } else {
            setActiveTab(null);
        }
        
        return () => {
            window.removeEventListener('hashchange', handleHashChange);
        };
    }, [tabNames, location]);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 786);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    return (
        <div className="flex flex-col min-h-screen bg-gray-50">
            {(!isMobile || !activeTab) && <Header />}
            {(!isMobile || !activeTab) && (
                <Path
                    currentPath={`${t('profile')}/${tabNames[activeTab] || ''}`}
                    pathTo="/profile"
                />
            )}

            <div className="flex flex-col md:flex-row flex-1 gap-5 px-4 md:px-[60px] lg:px-[120px] xl:px-[200px] py-6 items-start">
                {(!isMobile || !activeTab) && <Menu setActiveTab={setActiveTab} />}
                {activeTab
                    ? <div className="flex-1 min-w-0 w-full pb-20 md:pb-6">{tabComponents[activeTab]}</div>
                    : null
                }
            </div>

            {!hideBottomBar && (
                <MobileBottomBar activeTabName={activeTab} setActiveTab={setActiveTab} />
            )}
        </div>
    );
}

export default Main;